import type { IconButtonProps } from "@mui/material/IconButton";

import { m } from "framer-motion";
import { useEffect, useState } from "react";
import { useBoolean } from "minimal-shared/hooks";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";

import { Scrollbar } from "src/components/scrollbar";
import { varTap, varHover, transitionTap } from "src/components/animate";

import { NotificationItem } from "./notification-item";

import { RootState, AppDispatch } from "src/store/store";
import {
  doFetchNotificationList,
  doFetchUnreadNotificationCount,
} from "src/store/notification/notification.action";

import { FiBellOff } from "react-icons/fi";

// ----------------------------------------------------------------------

export type NotificationsDrawerProps = IconButtonProps;

export function NotificationsDrawer({
  sx,
  ...other
}: NotificationsDrawerProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Get data from Redux store
  const { notifications, meta, unreadCount, loading, unreadCountLoading } =
    useSelector((state: RootState) => state.notification);

  // Get notification list
  useEffect(() => {
    if (open) {
      dispatch(
        doFetchNotificationList({ page: currentPage, limit: itemsPerPage })
      );
    }
  }, [dispatch, open, currentPage]);

  // Add separate effect for unread count
  useEffect(() => {
    dispatch(doFetchUnreadNotificationCount());
  }, [dispatch]);

  // Handle pagination
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        pr: 1,
        pl: 2.5,
        minHeight: 68,
        display: "flex",
        alignItems: "center",
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      <IconButton onClick={onClose}>
        <SvgIcon>
          <path d="M18.3 5.71a1 1 0 0 0-1.42 0L12 10.59 7.12 5.71A1 1 0 1 0 5.7 7.12L10.59 12l-4.89 4.88a1 1 0 0 0 1.42 1.41L12 13.41l4.88 4.88a1 1 0 0 0 1.42-1.41L13.41 12l4.89-4.88a1 1 0 0 0 0-1.41z" />
        </SvgIcon>
      </IconButton>
    </Box>
  );

  const renderList = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress thickness={2} />
        </Box>
      );
    }

    if (!notifications.length) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
            color: "text.secondary",
            gap: 1.5,
          }}
        >
          <FiBellOff size={32} style={{ opacity: 0.6 }} />
          <Typography>No notifications found</Typography>
        </Box>
      );
    }

    return (
      <Scrollbar>
        <Box component="ul" sx={{ p: 0, m: 0, listStyle: "none" }}>
          {notifications.map((notification) => (
            <Box component="li" key={notification.id} sx={{ display: "flex" }}>
              <NotificationItem notification={notification} onClose={onClose} />
            </Box>
          ))}
        </Box>
      </Scrollbar>
    );
  };

  const renderPagination = () => {
    if (!notifications.length || !meta) return null;

    const totalPages = Math.ceil(meta.totalItems / meta.itemsPerPage);

    if (totalPages <= 1) return null;

    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size="small"
        />
      </Box>
    );
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label="Notifications button"
        onClick={() => {
          onOpen();
          dispatch(doFetchUnreadNotificationCount());
        }}
        sx={sx}
        {...other}
      >
        <Badge
          badgeContent={unreadCount ?? 0}
          color="error"
          invisible={unreadCount == null || unreadCount === 0}
          max={99}
        >
          <SvgIcon>
            {/* Bell icon */}
            <path
              fill="currentColor"
              d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7"
              opacity="0.5"
            />
            <path
              fill="currentColor"
              d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"
            />
          </SvgIcon>
        </Badge>
      </IconButton>

      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderHead()}

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1 }}>{renderList()}</Box>
          {renderPagination()}
        </Box>
      </Drawer>
    </>
  );
}
